// Type-only imports - safe for browser (types are erased at runtime)
import type { Logging, Sip, Network, Host, TlsContextInfo } from '@sbclite/core'
// Browser-safe schema imports
import type { SipAuthCredential } from '@sbclite/core/schemas'
import type { Info as UserPreferencesInfo } from '@sbclite/core/auth/user-preferences'
import type { Info as SystemPreferencesInfo } from '@sbclite/core/auth/system-preferences'
import type { Info as McpTokenInfo } from '@sbclite/core/auth/mcp-token'
import type { HostEngineAssociations, SipEngineOverview } from '@sbclite/core/container/api'
import type { UserListItem, CurrentUserInfo } from '../types/user'
import type { Certificate } from '../types/certificate'
import { getApiBaseUrl } from '../lib/runtime-config'

/** Query parameters for list/GET endpoints. */
type QueryParams = Record<string, string | number | boolean | undefined | null>

/** Pagination envelope returned by list endpoints. */
interface Pagination {
  total: number
  page: number
  pageSize: number
  totalPages: number
}

interface ApiResponse<T> {
  success: boolean
  data: T
  timestamp: string
}

interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

interface HttpResponse<T> {
  status: number
  data: T
}

export interface SipTrafficEvent {
  id: string;
  callId: string;
  eventType: string | null;
  direction: 'inbound' | 'outbound';
  transport: 'UDP' | 'TCP' | 'TLS' | 'WS' | 'WSS';
  timestamp: string;
  sipMethod?: string | null;
  responseCode?: number | null;
  reasonPhrase?: string | null;
  headers?: Record<string, string | string[]> | null;
  body?: string | null;
  rawMessage?: string | null;
  containerId: string;
  sipPeerId?: string | null;
  sipInterfaceId?: string | null;
  createdAt: string;
}

interface SipTrafficQueryResponse {
  data: SipTrafficEvent[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  redacted: boolean;
  traceEndTime: string;
  hasMore: boolean;
  order: 'asc' | 'desc';
}

interface TlsContextInventoryAnalysis {
  id: string;
  name: string;
  scope: 'sip_interface' | 'sip_peer';
  enabled: boolean;
  tlsContextId: string | null;
  status: 'migrated' | 'backfillable' | 'blocked';
  desiredContextName: string | null;
  blockers: Array<{ code: string; message: string }>;
}

interface TlsContextInventoryPayload {
  rows: TlsContextInventoryAnalysis[];
  summary: {
    totals: { all: number; enabled: number };
    migrated: number;
    backfillable: number;
    blocked: number;
    readyForCleanup: boolean;
    backfillTargets: TlsContextInventoryAnalysis[];
    blockers: TlsContextInventoryAnalysis[];
  };
}

interface TlsContextBackfillPayload {
  dryRun: boolean;
  appliedCount: number;
  before: TlsContextInventoryPayload['summary'];
  after: TlsContextInventoryPayload['summary'];
  cleanupReady: boolean;
}

interface SipProfileBackfillInventoryPayload {
  rows: Sip.SipProfileBackfillAnalysis[];
  summary: Sip.SipProfileBackfillSummary;
}

interface SipProfileBackfillPayload {
  dryRun: boolean;
  appliedCount: number;
  createdProfileCount: number;
  reusedProfileCount: number;
  before: Sip.SipProfileBackfillSummary;
  after: Sip.SipProfileBackfillSummary;
  cleanupReady: boolean;
}

export interface ApiErrorResponseLike {
  status: number
  statusText: string
  data: unknown
}

export class ApiRequestError extends Error {
  readonly response: ApiErrorResponseLike

  constructor(message: string, response: ApiErrorResponseLike) {
    super(message)
    this.name = 'ApiRequestError'
    this.response = response
  }
}

class ApiService {
  private readonly apiUrl: string

  constructor() {
    // In dev mode, Vite proxy handles /api routes to the backend.
    // In production, the API URL is provided at runtime via window.__APP_CONFIG__
    // (served by /config.js); empty means same-origin.
    this.apiUrl = getApiBaseUrl()
  }

  private buildUrl(path: string, params?: QueryParams): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    const relativePath = `${normalizedPath}`
    const requestUrl = this.apiUrl
      ? new URL(`${this.apiUrl}${relativePath}`)
      : new URL(relativePath, window.location.origin)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          requestUrl.searchParams.append(key, String(value))
        }
      })
    }

    return this.apiUrl
      ? requestUrl.toString()
      : `${requestUrl.pathname}${requestUrl.search}`
  }

  private extractErrorMessage(data: unknown, fallback: string): string {
    if (typeof data === 'string' && data.length > 0) {
      return data
    }

    if (typeof data === 'object' && data !== null) {
      const errorData = data as { message?: unknown; error?: unknown }
      if (typeof errorData.message === 'string' && errorData.message.length > 0) {
        return errorData.message
      }
      if (typeof errorData.error === 'string' && errorData.error.length > 0) {
        return errorData.error
      }
    }

    return fallback
  }

  private parseChallengeData(data: unknown): unknown {
    if (typeof data === 'string') {
      try {
        return JSON.parse(data)
      } catch {
        return null
      }
    }

    if (typeof data === 'object' && data !== null) {
      return data
    }

    return null
  }

  private throwIfManualDnsChallenge(status: number, data: unknown, statusText: string): void {
    if (status !== 202 || data === null || data === undefined) {
      return
    }

    const challengeData = this.parseChallengeData(data)
    if (!challengeData || typeof challengeData !== 'object') {
      return
    }

    const maybeMessage = (challengeData as { message?: unknown }).message
    let actualChallengeData: unknown = challengeData

    if (typeof maybeMessage === 'string') {
      try {
        actualChallengeData = JSON.parse(maybeMessage)
      } catch {
        actualChallengeData = challengeData
      }
    }

    if (typeof actualChallengeData !== 'object' || actualChallengeData === null) {
      return
    }

    const challengeType = (actualChallengeData as { type?: unknown }).type
    if (challengeType === 'manual_dns_challenge' || challengeType === 'dns_waiting') {
      throw new ApiRequestError(JSON.stringify(actualChallengeData), {
        status,
        statusText,
        data,
      })
    }
  }

  private async parseResponseData(response: Response): Promise<unknown> {
    const contentType = response.headers.get('content-type')
    if (contentType?.includes('application/json')) {
      try {
        return await response.json()
      } catch {
        return null
      }
    }

    const text = await response.text()
    if (!text) {
      return null
    }

    try {
      return JSON.parse(text)
    } catch {
      return text
    }
  }

  private async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    url: string,
    options?: {
      params?: QueryParams
      body?: unknown
    }
  ): Promise<HttpResponse<T>> {
    const requestUrl = this.buildUrl(url, options?.params)

    const headers: HeadersInit = {}
    let body: BodyInit | undefined

    if (options?.body !== undefined) {
      if (options.body instanceof FormData) {
        body = options.body
      } else {
        headers['Content-Type'] = 'application/json'
        body = JSON.stringify(options.body)
      }
    }

    const response = await fetch(requestUrl, {
      method,
      credentials: 'include',
      headers,
      body,
    })

    const responseData = await this.parseResponseData(response)

    this.throwIfManualDnsChallenge(response.status, responseData, response.statusText)

    if (response.status === 401) {
      // Redirect to login on 401 - Better Auth handles session refresh via cookies
      window.location.href = '/login'
    }

    if (!response.ok) {
      throw new ApiRequestError(this.extractErrorMessage(responseData, response.statusText || 'Request failed'), {
        status: response.status,
        statusText: response.statusText,
        data: responseData,
      })
    }

    return {
      status: response.status,
      data: responseData as T,
    }
  }

  // ========================================
  // BASE HTTP METHODS
  // ========================================

  async get<T>(url: string, params?: QueryParams): Promise<ApiResponse<T>> {
    const response = await this.request<T>('GET', url, { params })
    return {
      success: true,
      data: response.data,
      timestamp: new Date().toISOString()
    }
  }

  async post<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    const response = await this.request<T>('POST', url, { body: data })
    return {
      success: true,
      data: response.data,
      timestamp: new Date().toISOString()
    }
  }

  async put<T>(url: string, data: unknown): Promise<ApiResponse<T>> {
    const response = await this.request<T>('PUT', url, { body: data })
    return {
      success: true,
      data: response.data,
      timestamp: new Date().toISOString()
    }
  }

  async delete<T>(url: string, options?: { data?: unknown; params?: QueryParams }): Promise<ApiResponse<T>> {
    const response = await this.request<T>('DELETE', url, {
      body: options?.data,
      params: options?.params,
    })
    return {
      success: true,
      data: response.data,
      timestamp: new Date().toISOString()
    }
  }

  async patch<T>(url: string, data: unknown): Promise<ApiResponse<T>> {
    const response = await this.request<T>('PATCH', url, { body: data })
    return {
      success: true,
      data: response.data,
      timestamp: new Date().toISOString()
    }
  }

  async getPaginated<T>(
    url: string,
    page: number,
    pageSize: number,
    params?: QueryParams
  ): Promise<PaginatedResponse<T>> {
    const response = await this.request<PaginatedResponse<T>>('GET', url, {
      params: { page, pageSize, ...params },
    })
    return response.data
  }

  // ========================================
  // PEER GROUPS API
  // ========================================

  async getPeerGroups(params?: QueryParams): Promise<ApiResponse<Sip.PeerGroupInfo[]>> {
    const response = await this.get<{ success: boolean; data: Sip.PeerGroupInfo[]; pagination?: Pagination }>('/api/peer-groups', params)
    return {
      success: true,
      data: response.data.data || [],
      timestamp: response.timestamp
    }
  }

  async createPeerGroup(data: unknown): Promise<ApiResponse<Sip.PeerGroupInfo>> {
    const response = await this.post<{ success: boolean; data: Sip.PeerGroupInfo }>('/api/peer-groups', data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async deletePeerGroup(id: string): Promise<ApiResponse<void>> {
    return this.delete(`/api/peer-groups/${id}`)
  }

  // ========================================
  // ROUTING RULES API
  // ========================================

  async getRoutingRules(params?: QueryParams): Promise<ApiResponse<Sip.RoutingRuleInfo[]>> {
    return this.get('/api/routing-rules', params)
  }

  async createRoutingRule(data: unknown): Promise<ApiResponse<Sip.RoutingRuleInfo>> {
    return this.post('/api/routing-rules', data)
  }

  async updateRoutingRule(id: string, data: unknown): Promise<ApiResponse<Sip.RoutingRuleInfo>> {
    return this.put(`/api/routing-rules/${id}`, data)
  }

  async deleteRoutingRule(id: string): Promise<ApiResponse<void>> {
    return this.delete(`/api/routing-rules/${id}`)
  }

  // ========================================
  // SIP REGISTRATIONS API
  // ========================================

  // NOTE: the list feeds SipRegistrationsPage's local `Registration` view-model (shared across 5
  // components in types/registration.ts). Its element type is owned by the consumer until that
  // view-model is unified with Sip.RegistrationInfo, so the list stays loosely typed here.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getSipRegistrations(params?: QueryParams): Promise<ApiResponse<any[]>> {
    const response = await this.get<{ success: boolean; data: unknown[]; pagination?: Pagination }>('/api/sip-registrations', params)
    return {
      success: true,
      data: response.data.data || [],
      timestamp: response.timestamp
    }
  }

  async getSipRegistrationStatistics(): Promise<ApiResponse<any>> {
    const response = await this.get<{ success: boolean; data: any }>('/api/sip-registrations/statistics')
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async cleanupExpiredSipRegistrations(): Promise<ApiResponse<any>> {
    return this.post('/api/sip-registrations/cleanup')
  }

  async getSipRegistrationById(registrationId: string): Promise<ApiResponse<Sip.RegistrationInfo>> {
    const response = await this.get<{ success: boolean; data: Sip.RegistrationInfo }>(`/api/sip-registrations/${registrationId}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async getSipRegistrationByAor(aor: string): Promise<ApiResponse<Sip.RegistrationInfo>> {
    const encodedAor = encodeURIComponent(aor)
    const response = await this.get<{ success: boolean; data: Sip.RegistrationInfo }>(`/api/sip-registrations/aor/${encodedAor}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async deleteSipRegistration(registrationId: string): Promise<ApiResponse<void>> {
    return this.delete(`/api/sip-registrations/${registrationId}`)
  }

  // ========================================
  // SIP TRAFFIC LOGGING API
  // ========================================

  async getSipTrafficSettings(): Promise<Logging.SipTrafficSettingsInfo> {
    const response = await this.get<{ data: Logging.SipTrafficSettingsInfo }>('/api/sip-traffic-logging/settings');
    return response.data.data;
  }

  async updateSipTrafficSettings(updates: Partial<Pick<Logging.SipTrafficSettingsInfo, 'enabled' | 'logLevel' | 'logRawMessages' | 'logBodies' | 'forceFullReload' | 'gracefulRestart'>>): Promise<Logging.SipTrafficSettingsInfo> {
    const response = await this.put<{ data: Logging.SipTrafficSettingsInfo }>('/api/sip-traffic-logging/settings', updates);
    return response.data.data;
  }

  async enableSipTrafficLogging(): Promise<Logging.SipTrafficSettingsInfo> {
    const response = await this.post<{ data: Logging.SipTrafficSettingsInfo }>('/api/sip-traffic-logging/settings/enable');
    return response.data.data;
  }

  async disableSipTrafficLogging(): Promise<Logging.SipTrafficSettingsInfo> {
    const response = await this.post<{ data: Logging.SipTrafficSettingsInfo }>('/api/sip-traffic-logging/settings/disable');
    return response.data.data;
  }

  async getApplicationLogLevel(): Promise<{ applicationLogLevel: Logging.LogLevel; fromDatabase: boolean }> {
    const response = await this.get<{ data: { applicationLogLevel: Logging.LogLevel; fromDatabase: boolean } }>('/api/sip-traffic-logging/settings/application-level');
    return response.data.data;
  }

  async updateApplicationLogLevel(applicationLogLevel: Logging.LogLevel): Promise<{ applicationLogLevel: Logging.LogLevel }> {
    const response = await this.put<{ data: { applicationLogLevel: Logging.LogLevel } }>('/api/sip-traffic-logging/settings/application-level', { applicationLogLevel });
    return response.data.data;
  }

  async getSipTrafficMessages(params?: {
    page?: number;
    pageSize?: number;
    callId?: string;
    relatedCallIds?: string; // Comma-separated Call-IDs for B2BUA
    containerId?: string;
    sipPeerId?: string;
    // Comma-separated list supported by API (e.g. "UDP,TLS")
    transport?: string;
    // Comma-separated list supported by API (e.g. "INVITE,REGISTER")
    sipMethod?: string;
    direction?: 'inbound' | 'outbound';
    startTime?: string;
    endTime?: string;
    order?: 'asc' | 'desc';
  }): Promise<{
    data: SipTrafficEvent[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    redacted: boolean;
    traceEndTime: string;
    hasMore: boolean;
    order: 'asc' | 'desc';
  }> {
    // Remove undefined values from params to prevent them from being sent as query parameters
    const cleanParams = params ? Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== undefined)
    ) : undefined;
    
    const response = await this.get<SipTrafficQueryResponse>('/api/sip-traffic', cleanParams);
    return {
      data: response.data.data,
      total: response.data.total,
      page: response.data.page,
      pageSize: response.data.pageSize,
      totalPages: response.data.totalPages,
      redacted: response.data.redacted,
      traceEndTime: response.data.traceEndTime,
      hasMore: response.data.hasMore,
      order: response.data.order,
    };
  }

  async getSipTrafficByCallId(callId: string): Promise<{
    data: SipTrafficEvent[];
  }> {
    const response = await this.get<{ data: SipTrafficEvent[] }>(`/api/sip-traffic/${callId}`);
    return { data: response.data.data };
  }

  // ========================================
  // LOGGING API
  // ========================================

  async getLogs(params?: {
    level?: Logging.LogLevel;
    service?: string;
    component?: string;
    startTime?: string;
    endTime?: string;
    search?: string;
    page?: number;
    pageSize?: number;
    asOf?: string;
  }): Promise<ApiResponse<{
    logs: Logging.LogEntryInfo[];
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
    asOf: string;
    redacted: boolean;
  }>> {
    return this.get('/api/logging', params)
  }

  async getLogServices(): Promise<ApiResponse<{ services: string[] }>> {
    return this.get('/api/logging/services')
  }

  // ========================================
  // NETWORK DOMAIN - SIP INTERFACES
  // ========================================

  /**
   * List TLS contexts
   */
  async getTlsContexts(): Promise<ApiResponse<TlsContextInfo[]>> {
    const response = await this.get<{ success: boolean; data: TlsContextInfo[] }>('/api/tls-contexts')
    return {
      success: true,
      data: response.data.data || [],
      timestamp: response.timestamp,
    }
  }

  /**
   * Get TLS context by ID
   */
  async getTlsContext(id: string): Promise<ApiResponse<TlsContextInfo>> {
    const response = await this.get<{ success: boolean; data: TlsContextInfo }>(`/api/tls-contexts/${id}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp,
    }
  }

  /**
   * Create a TLS context
   */
  async createTlsContext(data: unknown): Promise<ApiResponse<TlsContextInfo>> {
    const response = await this.post<{ success: boolean; data: TlsContextInfo }>('/api/tls-contexts', data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp,
    }
  }

  /**
   * Update a TLS context
   */
  async updateTlsContext(id: string, data: unknown): Promise<ApiResponse<TlsContextInfo>> {
    const response = await this.put<{ success: boolean; data: TlsContextInfo }>(`/api/tls-contexts/${id}`, data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp,
    }
  }

  /**
   * Delete a TLS context
   */
  async deleteTlsContext(id: string): Promise<ApiResponse<TlsContextInfo>> {
    const response = await this.delete<{ success: boolean; data: TlsContextInfo }>(`/api/tls-contexts/${id}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp,
    }
  }

  /**
   * Retrieve TLS context inventory/backfill analysis.
   */
  async getTlsContextInventory(): Promise<ApiResponse<TlsContextInventoryPayload>> {
    const response = await this.get<{ success: boolean; data: TlsContextInventoryPayload }>('/api/tls-contexts/inventory')
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp,
    }
  }

  /**
   * Run TLS context backfill.
   */
  async runTlsContextBackfill(apply: boolean): Promise<ApiResponse<TlsContextBackfillPayload>> {
    const response = await this.post<{ success: boolean; data: TlsContextBackfillPayload }>(`/api/tls-contexts/inventory/backfill?apply=${apply ? 'true' : 'false'}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp,
    }
  }

  /**
   * Retrieve SIP profile inventory/backfill analysis.
   */
  async getSipProfileInventory(): Promise<ApiResponse<SipProfileBackfillInventoryPayload>> {
    const response = await this.get<{ success: boolean; data: SipProfileBackfillInventoryPayload }>('/api/sip-profiles/inventory')
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp,
    }
  }

  /**
   * Run SIP profile backfill.
   */
  async runSipProfileBackfill(apply: boolean): Promise<ApiResponse<SipProfileBackfillPayload>> {
    const response = await this.post<{ success: boolean; data: SipProfileBackfillPayload }>(`/api/sip-profiles/inventory/backfill?apply=${apply ? 'true' : 'false'}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp,
    }
  }

  /**
   * List all SIP interfaces
   */
  async getSipInterfaces(params?: {
    active?: boolean;
    protocol?: 'UDP' | 'TCP' | 'TLS';
  }): Promise<ApiResponse<Network.SipInterfaceInfo[]>> {
    const response = await this.get<{ data: Network.SipInterfaceInfo[] }>('/api/network/interfaces', params)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  /**
   * Get a single SIP interface
   */
  async getSipInterface(id: string): Promise<ApiResponse<Network.SipInterfaceInfo>> {
    const response = await this.get<{ data: Network.SipInterfaceInfo }>(`/api/network/interfaces/${id}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  /**
   * Create a new SIP interface
   */
  async createSipInterface(data: unknown): Promise<ApiResponse<Network.SipInterfaceInfo>> {
    const response = await this.post<{ data: Network.SipInterfaceInfo }>('/api/network/interfaces', data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  /**
   * Update a SIP interface
   */
  async updateSipInterface(id: string, data: unknown): Promise<ApiResponse<Network.SipInterfaceInfo>> {
    const response = await this.put<{ data: Network.SipInterfaceInfo }>(`/api/network/interfaces/${id}`, data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  /**
   * Delete a SIP interface
   */
  async deleteSipInterface(id: string): Promise<ApiResponse<Network.SipInterfaceInfo>> {
    const response = await this.delete<{ data: Network.SipInterfaceInfo }>(`/api/network/interfaces/${id}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  /**
   * Get peer and container profile usage for a SIP interface
   */
  async getSipInterfaceUsage(id: string): Promise<ApiResponse<Network.SipInterfaceUsageInfo>> {
    const response = await this.get<{ data: Network.SipInterfaceUsageInfo }>(`/api/network/interfaces/${id}/usage`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp,
    }
  }

  /**
   * Get peers assigned to an interface
   */
  async getInterfacePeers(interfaceId: string): Promise<ApiResponse<Sip.PeerInfo[]>> {
    const response = await this.get<{ data: Sip.PeerInfo[] }>(`/api/network/interfaces/${interfaceId}/peers`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  /**
   * Assign a peer to an interface
   */
  async assignPeerToInterface(interfaceId: string, peerId: string, options?: {
    priority?: number;
    weight?: number;
    enabled?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.post<{ data: any }>(
      `/api/network/interfaces/${interfaceId}/peers/${peerId}`,
      options || {}
    )
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  /**
   * Remove a peer from an interface
   */
  async removePeerFromInterface(interfaceId: string, peerId: string): Promise<ApiResponse<void>> {
    const response = await this.delete<{ data: void }>(`/api/network/interfaces/${interfaceId}/peers/${peerId}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  /**
   * Get interfaces assigned to a peer
   */
  async getPeerInterfaces(peerId: string): Promise<ApiResponse<Network.SipInterfaceInfo[]>> {
    const response = await this.get<{ data: Network.SipInterfaceInfo[] }>(`/api/network/peers/${peerId}/interfaces`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  // ========================================
  // CODEC GROUPS API
  // ========================================

  async getCodecGroups(params?: { 
    limit?: number; 
    offset?: number; 
    enabled?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- consumed via useCodecGroups' local CodecGroupOption view-model
  }): Promise<ApiResponse<any[]>> {
    const response = await this.get<{ success: boolean; data: unknown[]; pagination?: Pagination }>('/api/codec-groups', params)
    return {
      success: true,
      data: response.data.data || [],
      timestamp: response.timestamp
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- consumed via useCodecGroups' local CodecGroupOption view-model
  async getCodecGroupById(id: string): Promise<ApiResponse<any>> {
    const response = await this.get<{ success: boolean; data: unknown }>(`/api/codec-groups/${id}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async getDefaultCodecGroup(): Promise<ApiResponse<Sip.CodecGroupInfo>> {
    const response = await this.get<{ success: boolean; data: Sip.CodecGroupInfo }>('/api/codec-groups/default')
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async createCodecGroup(data: unknown): Promise<ApiResponse<Sip.CodecGroupInfo>> {
    const response = await this.post<{ success: boolean; data: Sip.CodecGroupInfo }>('/api/codec-groups', data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async updateCodecGroup(id: string, data: unknown): Promise<ApiResponse<Sip.CodecGroupInfo>> {
    const response = await this.put<{ success: boolean; data: Sip.CodecGroupInfo }>(`/api/codec-groups/${id}`, data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async deleteCodecGroup(id: string, options?: { force?: boolean }): Promise<ApiResponse<void>> {
    const params = new URLSearchParams()
    if (options?.force) {
      params.set('force', 'true')
    }
    const suffix = params.toString() ? `?${params.toString()}` : ''
    return this.delete(`/api/codec-groups/${id}${suffix}`)
  }

  // ========================================
  // CERTIFICATES API
  // ========================================

  async getCertificates(params?: {
    limit?: number;
    offset?: number;
    certificateType?: 'server' | 'client' | 'ca' | 'intermediate';
  }): Promise<ApiResponse<import('@sbclite/core/schemas').PublicCertificateInfo[]>> {
    const rows: import('@sbclite/core/schemas').PublicCertificateInfo[] = [];
    let offset = params?.offset ?? 0;
    let more = true;
    while (more) {
      const response = await this.get<{ success: boolean; data: import('@sbclite/core/schemas').PublicCertificateInfo[]; pagination: { hasMore: boolean } }>('/api/certificates', { ...params, limit: params?.limit ?? 100, offset });
      if (!response.data.success) throw new Error('Unable to load certificates');
      rows.push(...response.data.data);
      offset += response.data.data.length;
      more = params?.limit === undefined && response.data.pagination.hasMore;
      if (more && !response.data.data.length) throw new Error('Certificate pagination did not advance');
    }
    return {
      success: true,
      data: rows,
      timestamp: new Date().toISOString()
    }
  }

  async getCertificateById(id: string): Promise<ApiResponse<import('@sbclite/core/schemas').PublicCertificateInfo>> {
    const response = await this.get<{ success: boolean; data: import('@sbclite/core/schemas').PublicCertificateInfo }>(`/api/certificates/${encodeURIComponent(id)}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  // ========================================
  // HOSTS API
  // ========================================

  async getHosts(): Promise<ApiResponse<Host.HostInfo[]>> {
    const response = await this.get<{ success: boolean; data: Host.HostInfo[] }>('/api/hosts')
    return {
      success: true,
      data: response.data.data || [],
      timestamp: response.timestamp
    }
  }

  async getSipEngineOverview(containerId: string): Promise<ApiResponse<SipEngineOverview>> {
    return this.get(`/api/container-profiles/containers/${encodeURIComponent(containerId)}/overview`)
  }

  async getHostEngineAssociations(hostId: string, params?: { limit?: number; offset?: number }): Promise<ApiResponse<HostEngineAssociations>> {
    return this.get(`/api/hosts/${encodeURIComponent(hostId)}/engines`, params)
  }

  async getHostById(id: string): Promise<ApiResponse<Host.HostInfo>> {
    const response = await this.get<{ success: boolean; data: Host.HostInfo }>(`/api/hosts/${id}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async getDefaultHost(): Promise<ApiResponse<Host.HostInfo>> {
    const response = await this.get<{ success: boolean; data: Host.HostInfo }>(`/api/hosts/default`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async createHost(data: unknown): Promise<ApiResponse<Host.HostInfo>> {
    const response = await this.post<{ success: boolean; data: Host.HostInfo }>(`/api/hosts`, data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async updateHost(id: string, data: unknown): Promise<ApiResponse<Host.HostInfo>> {
    const response = await this.put<{ success: boolean; data: Host.HostInfo }>(`/api/hosts/${id}`, data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async deleteHost(id: string): Promise<ApiResponse<void>> {
    return this.delete(`/api/hosts/${id}`)
  }

  // ========================================
  // USER PREFERENCES API
  // ========================================

  async getUserPreferences(): Promise<ApiResponse<UserPreferencesInfo>> {
    const response = await this.get<{ success: boolean; data: UserPreferencesInfo }>('/api/user/preferences')
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async updateUserPreferences(data: {
    theme?: 'light' | 'dark' | 'system';
    timezone?: string;
    sidebarCollapsed?: boolean;
    tablePageSize?: number;
  }): Promise<ApiResponse<UserPreferencesInfo>> {
    const response = await this.put<{ success: boolean; data: UserPreferencesInfo }>('/api/user/preferences', data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async getCurrentUser(): Promise<ApiResponse<CurrentUserInfo>> {
    const response = await this.get<{ success: boolean; data: CurrentUserInfo }>('/api/user/me')
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async updateUserProfile(data: { name: string }): Promise<ApiResponse<UserListItem>> {
    const response = await this.put<{ success: boolean; data: UserListItem }>('/api/user/profile', data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  // ========================================
  // SYSTEM PREFERENCES API (Admin only)
  // ========================================

  async getSystemPreferences(): Promise<ApiResponse<SystemPreferencesInfo>> {
    const response = await this.get<{ success: boolean; data: SystemPreferencesInfo }>('/api/system/preferences')
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async updateSystemPreferences(data: {
    approvedDomains?: string[];
    registrationMode?: 'open' | 'restricted' | 'invite_only';
    requireEmailVerification?: boolean;
    sessionTimeoutHours?: number;
    maxUsers?: number | null;
  }): Promise<ApiResponse<SystemPreferencesInfo>> {
    const response = await this.put<{ success: boolean; data: SystemPreferencesInfo }>('/api/system/preferences', data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async addApprovedDomain(domain: string): Promise<ApiResponse<SystemPreferencesInfo>> {
    const response = await this.post<{ success: boolean; data: SystemPreferencesInfo }>('/api/system/preferences/domains', { domain })
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async removeApprovedDomain(domain: string): Promise<ApiResponse<SystemPreferencesInfo>> {
    const response = await this.delete<{ success: boolean; data: SystemPreferencesInfo }>(`/api/system/preferences/domains/${encodeURIComponent(domain)}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  // ========================================
  // USER MANAGEMENT API (Admin only)
  // ========================================

  async getUsers(): Promise<ApiResponse<UserListItem[]>> {
    const response = await this.get<{ success: boolean; data: UserListItem[] }>('/api/system/users')
    return {
      success: true,
      data: response.data.data || [],
      timestamp: response.timestamp
    }
  }

  async updateUserRole(userId: string, role: 'admin' | 'user'): Promise<ApiResponse<UserListItem>> {
    const response = await this.put<{ success: boolean; data: UserListItem }>(`/api/system/users/${userId}/role`, { role })
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  // ========================================
  // REGISTRATION VALIDATION API
  // ========================================

  async validateRegistration(email: string): Promise<ApiResponse<{
    allowed: boolean;
    isFirstUser: boolean;
    role?: string;
    error?: string;
  }>> {
    const response = await this.post<{ success: boolean; allowed: boolean; isFirstUser: boolean; role?: string; error?: string }>('/api/registration/validate', { email })
    return {
      success: true,
      data: response.data,
      timestamp: new Date().toISOString()
    }
  }

  // ========================================
  // EMAIL CONFIGURATION API (Admin only)
  // ========================================

  async getEmailConfig(): Promise<ApiResponse<{
    resendApiKeyConfigured: boolean;
    resendApiKeyMasked: string | null;
    resendEnvLocked: boolean;
    emailSenderDomain: string;
    emailSenderName: string;
    emailVerificationEnabled: boolean;
  }>> {
    const response = await this.get<{ success: boolean; data: {
      resendApiKeyConfigured: boolean;
      resendApiKeyMasked: string | null;
      resendEnvLocked: boolean;
      emailSenderDomain: string;
      emailSenderName: string;
      emailVerificationEnabled: boolean;
    } }>('/api/system/preferences/email')
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async updateEmailConfig(data: {
    resendApiKey?: string | null;
    emailSenderDomain?: string;
    emailSenderName?: string;
    emailVerificationEnabled?: boolean;
  }): Promise<ApiResponse<{ resendApiKeyConfigured: boolean; resendApiKeyMasked: string | null; resendEnvLocked: boolean; emailSenderDomain: string; emailSenderName: string; emailVerificationEnabled: boolean }>> {
    const response = await this.put<{ success: boolean; data: { resendApiKeyConfigured: boolean; resendApiKeyMasked: string | null; resendEnvLocked: boolean; emailSenderDomain: string; emailSenderName: string; emailVerificationEnabled: boolean } }>('/api/system/preferences/email', data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async sendTestEmail(): Promise<ApiResponse<{ message: string }>> {
    const response = await this.post<{ success: boolean; message: string }>('/api/system/preferences/email/test')
    return {
      success: true,
      data: { message: response.data.message },
      timestamp: new Date().toISOString()
    }
  }

  // ========================================
  // MCP TOKEN API (Admin only)
  // ========================================

  async getMcpTokens(): Promise<ApiResponse<McpTokenInfo[]>> {
    const response = await this.get<{ success: boolean; data: McpTokenInfo[] }>('/api/system/mcp-tokens')
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async createMcpToken(data: { name: string }): Promise<ApiResponse<{ token: string; record: McpTokenInfo }>> {
    const response = await this.post<{ success: boolean; data: { token: string; record: McpTokenInfo } }>('/api/system/mcp-tokens', data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async revokeMcpToken(id: string): Promise<ApiResponse<McpTokenInfo>> {
    const response = await this.delete<{ success: boolean; data: McpTokenInfo }>(`/api/system/mcp-tokens/${id}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  // ========================================
  // EMAIL VERIFICATION API
  // ========================================

  async checkResendVerification(): Promise<ApiResponse<{ email: string; message: string }>> {
    const response = await this.post<{ success: boolean; email: string; message: string }>('/api/user/resend-verification')
    return {
      success: true,
      data: { email: response.data.email, message: response.data.message },
      timestamp: new Date().toISOString()
    }
  }

  // ========================================
  // SIP AUTHENTICATION API
  // ========================================

  async getSipAuthCredentials(params?: {
    limit?: number;
    offset?: number;
    hostId?: string;
    enabled?: boolean;
  }): Promise<ApiResponse<{
    credentials: SipAuthCredential[];
    pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  }>> {
    const response = await this.get<{ success: boolean; data: { credentials: SipAuthCredential[]; pagination: { total: number; limit: number; offset: number; hasMore: boolean } } }>('/api/sip-authentication/credentials', params)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async getSipAuthCredential(username: string, hostId?: string): Promise<ApiResponse<SipAuthCredential>> {
    const params = hostId ? { hostId } : undefined
    const response = await this.get<{ success: boolean; data: SipAuthCredential }>(`/api/sip-authentication/credentials/${encodeURIComponent(username)}`, params)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async createSipAuthCredential(data: {
    username: string;
    password: string;
    hostId: string;
    peerId?: string;
    enabled?: boolean;
    metadata?: Record<string, unknown>;
  }): Promise<ApiResponse<SipAuthCredential>> {
    const response = await this.post<{ success: boolean; data: SipAuthCredential }>('/api/sip-authentication/credentials', data)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async updateSipAuthCredential(username: string, data: {
    password?: string;
    hostId?: string;
    peerId?: string | null;
    enabled?: boolean;
    metadata?: Record<string, unknown>;
  }, currentHostId?: string): Promise<ApiResponse<SipAuthCredential>> {
    const params = currentHostId ? `?hostId=${encodeURIComponent(currentHostId)}` : ''
    const response = await this.put<{ success: boolean; data: SipAuthCredential }>(
      `/api/sip-authentication/credentials/${encodeURIComponent(username)}${params}`,
      data
    )
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async deleteSipAuthCredential(username: string, hostId?: string): Promise<ApiResponse<void>> {
    const params = hostId ? `?hostId=${encodeURIComponent(hostId)}` : ''
    return this.delete(`/api/sip-authentication/credentials/${encodeURIComponent(username)}${params}`)
  }

  async unlockSipAuthAccount(username: string, hostId?: string): Promise<ApiResponse<SipAuthCredential>> {
    const params = hostId ? `?hostId=${encodeURIComponent(hostId)}` : ''
    const response = await this.post<{ success: boolean; data: SipAuthCredential }>(
      `/api/sip-authentication/credentials/${encodeURIComponent(username)}/unlock${params}`
    )
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async getSipAuthStats(): Promise<ApiResponse<{
    totalCredentials: number;
    activeCredentials: number;
    lockedCredentials: number;
    totalSuccessfulAuths: number;
    totalFailedAttempts: number;
  }>> {
    const response = await this.get<{ success: boolean; data: { totalCredentials: number; activeCredentials: number; lockedCredentials: number; totalSuccessfulAuths: number; totalFailedAttempts: number } }>('/api/sip-authentication/stats')
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  async cleanupSipAuth(): Promise<ApiResponse<{
    expiredNonces: number;
    unlockedAccounts: number;
    duration: number;
  }>> {
    const response = await this.post<{ success: boolean; data: { expiredNonces: number; unlockedAccounts: number; duration: number } }>('/api/sip-authentication/cleanup')
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  // ========================================
  // CALL HISTORY API
  // ========================================

  /**
   * Get call history with pagination and filtering
   */
  async getCallHistory(params?: {
    limit?: number;
    offset?: number;
    status?: 'INVITE' | 'TRYING' | 'RINGING' | 'ANSWERED' | 'TERMINATED' | 'FAILED';
    direction?: 'inbound' | 'outbound';
    transport?: 'UDP' | 'TCP' | 'TLS';
  }): Promise<ApiResponse<CallHistoryResult>> {
    const response = await this.get<{ success: boolean; data: CallHistoryResult }>('/api/statistics/calls', params)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }

  /**
   * Get call by ID
   */
  async getCallById(callId: string): Promise<ApiResponse<CallHistoryEntry | null>> {
    const response = await this.get<{ success: boolean; data: CallHistoryEntry | null }>(`/api/sip-calls/${callId}`)
    return {
      success: true,
      data: response.data.data,
      timestamp: response.timestamp
    }
  }
}

// Call history entry type
export interface CallHistoryEntry {
  id: string;
  callId: string;
  containerId: string;
  fromUri: string;
  toUri: string;
  status: 'INVITE' | 'TRYING' | 'RINGING' | 'ANSWERED' | 'TERMINATED' | 'FAILED';
  direction: 'inbound' | 'outbound';
  transport: 'UDP' | 'TCP' | 'TLS';
  sipMethod: string;
  startTime: string;
  endTime: string | null;
  duration: number | null;  // in seconds
  terminationReason: string | null;
  sipCallData: {
    routingRuleId?: string;
    routingRuleName?: string;
    sourcePeerId?: string;
    sourcePeerName?: string;
    sourcePeerType?: string;
    destinationPeerId?: string;
    destinationPeerName?: string;
    destinationPeerType?: string;
    selectedCodec?: string;
    commonCodecs?: string[];
    userAgent?: string;
    sourceIp?: string;
    sourcePort?: number;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface CallHistoryResult {
  calls: CallHistoryEntry[];
  total: number;
  summary: {
    byStatus: Record<string, number>;
    byDirection: Record<string, number>;
    byTransport: Record<string, number>;
    byTerminationReason: Record<string, number>;
    duration: {
      avgSeconds: number;
      maxSeconds: number;
      minSeconds: number;
      totalCallsWithDuration: number;
    };
  };
}

// Re-export SIP Authentication types from schemas (browser-safe)
export type { SipAuthCredential, SipAuthStats, SipAuthCleanupResult } from '@sbclite/core/schemas'

export const api = new ApiService()
